import type {
  ArgsProps,
  JointContent,
  MessageInstance,
  NoticeType,
} from 'antd/es/message/interface'

type AnnouncePriority = 'polite' | 'assertive'

type AccessibleArgsProps = ArgsProps & {
  props?: Record<string, unknown>
}

function extractMessageText(content: JointContent): string {
  if (typeof content === 'string' || typeof content === 'number') {
    return String(content)
  }

  if (typeof content === 'object' && content !== null && 'content' in content) {
    return extractMessageText(content.content as JointContent)
  }

  return ''
}

function getAnnouncePriority(type?: NoticeType): AnnouncePriority {
  return type === 'error' ? 'assertive' : 'polite'
}

function withVisualOnlyProps(config: AccessibleArgsProps): AccessibleArgsProps {
  return {
    ...config,
    props: {
      'aria-hidden': true,
      ...config.props,
    },
  }
}

function toArgsProps(
  content: JointContent,
  type?: NoticeType
): AccessibleArgsProps {
  if (typeof content === 'object' && content !== null && 'content' in content) {
    return {
      ...content,
      type: content.type ?? type,
    }
  }

  return { content, type }
}

export function createAccessibleMessageApi(
  messageApi: MessageInstance,
  announce: (message: string, priority?: AnnouncePriority) => void
): MessageInstance {
  const open = (config: AccessibleArgsProps) => {
    const messageText = extractMessageText(config)
    if (messageText) {
      announce(messageText, getAnnouncePriority(config.type))
    }

    return messageApi.open(withVisualOnlyProps(config))
  }

  const wrapType =
    (type: NoticeType): MessageInstance['success'] =>
    (jointContent, duration, onClose) => {
      const config = toArgsProps(jointContent, type)

      if (typeof duration === 'function') {
        return open({ ...config, onClose: duration })
      }

      return open({
        ...config,
        duration,
        onClose,
      })
    }

  return {
    ...messageApi,
    open,
    info: wrapType('info'),
    success: wrapType('success'),
    error: wrapType('error'),
    warning: wrapType('warning'),
    loading: wrapType('loading'),
  }
}
